import {
  Gym, GymTypes, HandledError, NotFoundError,
} from 'fitbeat.models';
import { WarmupWarmdownConfig } from '../../config/WarmupwarmdownConfig';
import {
  AreaCategory,
  hasExerciseForZoneScreen,
  isStaticFunctionalZone,
  staticFunctionalAreaZones,
} from '../../config/zone-mappings';
import { IHomeQuarterZoneValidation, UserProfile, UserWorkoutSessionWithDisplayInfo } from '../../entities';
import {
  CardioOrResistance, Exercise, ExerciseFormat,
  MotivationalVoiceTipType,
} from '../../entities/exercise/exercise';
import { GymState } from '../../entities/gymschedule/gymstate';
import { WorkoutSettings } from '../../entities/user/workoutsettings';
import { ExerciseSelection } from '../../entities/workout/exerciseselection';
import { WorkoutSelection } from '../../entities/workout/workoutselection';
import { SessionSequence, SessionSequenceEntry, UserWorkoutSession } from '../../entities/workoutsession';
import { HomeWorkoutSequence } from '../../entities/workoutsession/homeworkoutsequence';
import { UserExerciseSession } from '../../entities/workoutsession/userexercisesession';
import { DbGymRepository, IUserProfileRepository, IWorkoutRepository } from '../../persistence';
import { IExerciseRepository } from '../../persistence/repositories/interfaces/iexercise-repository';
import { HomeBookingsManager } from '../bookings/homebookingsmanager';
import {
  EquipmentWeightCalculator,
  IUserInfoForEquipmentWeight,
} from '../equipment-weight-recommendations/EquipmentWeightCalculator';
import { ScheduleExercises } from '../gymscheduling/scheduleexercises';
import { SessionSequenceStrategyContext } from '../sessionsequence/sessionsequencestrategycontext';
import {
  CurrentWorkoutSelectionManager,
  HomeQuarterCalculator, isHomeBoxingWorkout, PauseBreakExerciseSelector,
  VoiceTipsSelector, WarmupAndWarmdownSelector, WorkoutSelectionManager,
} from '../workoutselection';
import { MotiTipContext } from '../workoutselection/motiTipsStrategy/motiTipContext';
import { ThreeMotiTipsPerExercise } from '../workoutselection/motiTipsStrategy/threeMotiTipsPerExercise';
import { PseudoRandomGenerator } from '../workoutselection/pseudorandomgenerator';
import { InfluencerWorkoutSelectionStrategy } from './InfluencerWorkoutSelectionStrategy';
import { IWorkoutSelectionStrategy } from './IWorkoutSelectionStrategy';
import { UserBasedWorkoutSelectionStrategy } from './UserBasedWorkoutSelectionStrategy';
import { WorkoutType } from '../../config';

// responsible for just in time population of actual exercises for gym state
// based on workout selection and warmups/downs

interface IStudioHydrationParams {
  activeWorkoutSelection: WorkoutSelection | undefined;
  userExerciseSession: UserExerciseSession;
  settings: WorkoutSettings;
  uniqueWarmupsAndWarmdownsForToday: Exercise[];
  breaks: Exercise[][];
  gym: Gym;
  isNewHomeWorkout: false;
  userInfoForEquipmentWeight: IUserInfoForEquipmentWeight;
}
export class WorkoutHydration {
  private homeQuarterCalculator: HomeQuarterCalculator;

  private superSetRepetition = 1;

  constructor(
    private exerciseRepository: IExerciseRepository,
    private workoutRepository: IWorkoutRepository,
    private userProfileRepository: IUserProfileRepository,
    private logger: any,
    private currentWorkoutSelectionManager: CurrentWorkoutSelectionManager,
    private workoutSelectionManager: WorkoutSelectionManager,
  ) {
    this.homeQuarterCalculator = new HomeQuarterCalculator();
  }

  public async GetHydratedHomeWorkout(
    workoutId: string,
    requestTime: Date,
    userDeviceTime: Date,
    timezone: string,
    user: UserProfile,
    playbackSpeed?: number,
  ): Promise<HomeWorkoutSequence> {
    const workout = await this.workoutRepository.getWorkoutSessionFromId(workoutId);
    if (!workout) {
      throw NotFoundError.Workout(workoutId);
    }

    if (UserWorkoutSession.isStreamingWorkoutAndNotHost(workout) && workout.pairedSessionReference) {
      const liveStreamWorkout = await this.workoutRepository.getWorkoutSessionFromId(workout.pairedSessionReference);
      workout.livestreamOptions = liveStreamWorkout?.livestreamOptions;
    }

    const gym = DbGymRepository.getByName(workout.gymName);
    if (gym.type !== GymTypes.Home) {
      throw new Error(`Cannot hydrate ${gym.type} workout type`);
    }

    const workoutSelectionStrategy = this.getWorkoutSelectionStrategy(workout);
    return this.hydrateHomeWorkoutSequence(
      workout,
      requestTime,
      userDeviceTime,
      timezone,
      workoutSelectionStrategy,
      user,
      playbackSpeed,
    );
  }

  public async getHomeInstructorForThisWorkout(
    workoutSession: UserWorkoutSession,
    workoutStartTime: Date,
    workoutCount: number,
  ) {
    if (workoutSession.settings.cardioOrResistance === CardioOrResistance.Balance) {
      return 'Eliza';
    }
    if (workoutSession.settings.homeWorkoutInstructor === 'Eliza') {
      delete workoutSession.settings.homeWorkoutInstructor;
    }
    const instructorOnWorkout = this.checkIfInstructorSetOnWorkout(workoutSession.settings);
    if (instructorOnWorkout) {
      this.logger.info(`Using instructor from workout: ${instructorOnWorkout}`);
      return instructorOnWorkout;
    }

    const instructorOnUserProfile = await this.checkIfInstructorSetOnUserProfile(workoutSession.userProfileId);
    if (instructorOnUserProfile) {
      this.logger.info(`Using default instructor from user profile: ${instructorOnUserProfile}`);
      return instructorOnUserProfile;
    }

    if (WarmupWarmdownConfig.isTutorialWarmupNeeded(workoutCount)) {
      const tutorialWarmup = WarmupWarmdownConfig.getTutorialWarmup(workoutCount);
      if (tutorialWarmup) {
        this.logger.info(`Using instructor for tutorial warmup: ${tutorialWarmup.instructor}`);
        return tutorialWarmup.instructor;
      }
    }

    return this.getHomeInstructorForToday(workoutStartTime);
  }

  public async hydrateHomeWorkoutSequence(
    userWorkoutSession: UserWorkoutSession,
    requestTime: Date,
    userDeviceTime: Date,
    timezone: string,
    workoutSelectionStrategy: IWorkoutSelectionStrategy,
    user: UserProfile,
    playbackSpeed?: number,
  ): Promise<HomeWorkoutSequence> {
    userWorkoutSession.exerciseSessions = [];
    const gymType = GymTypes.Home;
    ScheduleExercises.scheduleExercisesInWorkout(userWorkoutSession, 1, gymType);
    const workoutCount = await this.workoutRepository.getUserWorkoutCount(userWorkoutSession.userProfileId);

    const instructor = await this.getHomeInstructorForThisWorkout(
      userWorkoutSession,
      userWorkoutSession.startTime,
      workoutCount,
    );
    const { settings } = userWorkoutSession;
    this.setInstructorOnWorkoutSettings(settings, instructor);

    const uniqueWarmupsAndWarmdownsForToday = await WarmupAndWarmdownSelector.getWarmupsWarmdownsForToday(
      this.exerciseRepository,
      gymType,
      requestTime,
      instructor,
      workoutCount,
      settings,
    );

    const uniqueBreaksForToday = await PauseBreakExerciseSelector.getBreaksForToday(
      this.exerciseRepository,
      gymType,
      requestTime,
      instructor,
    );

    const motiTipsMap = await VoiceTipsSelector.getMotiTipsForToday(
      this.exerciseRepository,
      gymType,
      requestTime,
      instructor,
    );

    const sequences: SessionSequenceEntry[] = [];
    if (!isHomeBoxingWorkout(userWorkoutSession.settings, gymType)) {
      userWorkoutSession.settings.allowBagContact = false;
    }

    const userWorkoutSessionForWorkoutSelection = await this.getUserWorkoutSessionForWorkoutSelection(
      userWorkoutSession,
    );

    const activeWorkoutSelection = await workoutSelectionStrategy.getWorkoutSelectionForWorkout(
      settings,
      gymType,
      requestTime,
      timezone,
      userWorkoutSessionForWorkoutSelection,
    );

    if (!activeWorkoutSelection) {
      throw new HandledError('No active workout session found.');
    }

    const homeQuarterValidation = this.homeQuarterCalculator.calculate(activeWorkoutSelection);
    const previousExerciseSessions: UserExerciseSession[] = [];
    for (const session of userWorkoutSession.exerciseSessions) {
      const sequence = this.getHydratedHomeSequence(
        activeWorkoutSelection,
        session,
        settings,
        uniqueWarmupsAndWarmdownsForToday,
        uniqueBreaksForToday,
        homeQuarterValidation,
        user,
        previousExerciseSessions,
        playbackSpeed,
      );
      const flattenedSequence = SessionSequenceStrategyContext.flattenSequence(sequence);
      this.hydrateVoiceTips(
        flattenedSequence,
        session,
        settings,
        motiTipsMap,
        !!userWorkoutSession.isStreamingWorkout,
        !!userWorkoutSession.livestreamOptions?.streamerWillDropOffAfterTalk,
      );
      sequences.push(...flattenedSequence);
      previousExerciseSessions.push(session);
    }

    this.addPreWarmupTalkSequenceIfNeeded(sequences, userWorkoutSession, playbackSpeed);
    this.addPreWarmUpSequence(sequences, userWorkoutSession, gymType, playbackSpeed);

    if (activeWorkoutSelection) {
      if (!userWorkoutSession.homeWorkoutStartedAt && userWorkoutSession.isInstant) {
        HomeBookingsManager.setStartAndEndTime(userWorkoutSession, requestTime, playbackSpeed);
      }

      if (!userWorkoutSession.isInstant) {
        userWorkoutSession.homeWorkoutStartedAt = new Date();
      }

      userWorkoutSession.workoutSelectionId = activeWorkoutSelection.uniqueReference;
      await this.workoutRepository.update(userWorkoutSession);
    }

    const gym = DbGymRepository.getByName(userWorkoutSession.gymName);
    const isDemoBooking = userWorkoutSession.isDemo ? userWorkoutSession.isDemo : false;
    const timeAdjustedEndTime = this.getEndTimeAdjustToUserDeviceTime(
      userWorkoutSession.endTime,
      userDeviceTime,
      requestTime,
    );
    const enableInstructorChange = this.canChangeInstructor(workoutCount, userWorkoutSession.settings);
    const workoutDurationInMs = UserWorkoutSession.getWorkoutDurationInMs(userWorkoutSession);
    return new HomeWorkoutSequence(
      userWorkoutSession.startTime,
      timeAdjustedEndTime,
      sequences,
      userWorkoutSession.uniqueReference,
      userWorkoutSession.settings,
      userWorkoutSession.workoutType,
      gym,
      userWorkoutSession.startTimeOfFirstExercise,
      userWorkoutSession.isInstant,
      workoutDurationInMs,
      isDemoBooking,
      UserWorkoutSession.getOpenInviteReference(userWorkoutSession),
      userWorkoutSession.originalEndTime,
      userWorkoutSession.homeWorkoutStartedAt,
      userWorkoutSession.isStreamingWorkout,
      userWorkoutSession.pairedSessionReference,
      userWorkoutSession.pairedSessionUserProfileId,
      enableInstructorChange,
      userWorkoutSession.livestreamOptions,
    );
  }

  public async hydrateGymStateFromWorkoutSelection(
    gymState: GymState[],
    gym: Gym,
    date: Date,
    workoutSelectionStrategy: IWorkoutSelectionStrategy,
  ) {
    const uniqueWarmupsAndWarmdownsForToday = await WarmupAndWarmdownSelector.getWarmupsWarmdownsForToday(
      this.exerciseRepository,
      gym.type,
      date,
    );
    const cachedWorkoutSelection: { [key: string]: WorkoutSelection | undefined } = {};

    for (const state of gymState) {
      for (const session of state.sessions) {
        if (session.isEmpty()) {
          continue;
        }
        try {
          const parentWorkoutSession = session.parentWorkoutSession as UserWorkoutSessionWithDisplayInfo;
          const { settings, uniqueReference } = parentWorkoutSession;
          if (!cachedWorkoutSelection[uniqueReference]) {
            const activeWorkoutSelection = await this.determineStudioActiveWorkoutSelection(
              parentWorkoutSession,
              workoutSelectionStrategy,
              settings,
              gym,
              date,
            );
            cachedWorkoutSelection[uniqueReference] = activeWorkoutSelection;
          }

          const isNewHomeWorkout = false;
          const noDynamicBreaks = [[]];
          const userInfoForEquipmentWeight = {
            gender: parentWorkoutSession.gender,
            strengthRatioForMuscleGroups: parentWorkoutSession.strengthRatioForMuscleGroups,
            uid: parentWorkoutSession.userProfileId,
          };
          this.hydrateStudioExerciseSessionExerciseForSelection({
            userInfoForEquipmentWeight,
            activeWorkoutSelection: cachedWorkoutSelection[uniqueReference],
            userExerciseSession: session,
            breaks: noDynamicBreaks,
            isNewHomeWorkout,
            settings,
            uniqueWarmupsAndWarmdownsForToday,
            gym,
          });
        } catch (e) {
          this.logger.error(`failed to hydrate workout ${session.parentWorkoutSession.uniqueReference}`);
        }
      }
    }
  }

  public hydrateVoiceTips(
    flattenedSequence: SessionSequenceEntry[],
    session: UserExerciseSession,
    settings: WorkoutSettings,
    motiVoiceTips: Map<string, { [key: string]: Exercise[] }>,
    isStreamingWorkout: boolean,
    streamerWillDropOffAfterTalk: boolean,
  )
    : SessionSequenceEntry[] {
    const motiVoiceTipsCopy = new Map(motiVoiceTips);
    const motivs = motiVoiceTipsCopy.get(settings.cardioOrResistance);
    const motiTips = {
      Q1: motivs && motivs[MotivationalVoiceTipType.MotivQ1],
      Q2: motivs && motivs[MotivationalVoiceTipType.MotivQ2],
      Q3: motivs && motivs[MotivationalVoiceTipType.MotivQ3],
    };

    const motiTipContext = new MotiTipContext(new ThreeMotiTipsPerExercise());
    const isLiveStreamWorkoutAndStreamerWillStayForDurationOfWorkout = isStreamingWorkout
      && !streamerWillDropOffAfterTalk;
    return flattenedSequence
      .map((entry: SessionSequenceEntry) => motiTipContext
        .useStrategy(entry, session, settings, motiTips, isLiveStreamWorkoutAndStreamerWillStayForDurationOfWorkout));
  }

  public hydrateStudioExerciseSessionExerciseForSelection(args: IStudioHydrationParams) {
    const selectionToUse = this.getStudioExerciseSelection(
      args.activeWorkoutSelection,
      args.userExerciseSession,
      args.settings,
      args.uniqueWarmupsAndWarmdownsForToday,
      args.breaks,
      args.gym,
      args.isNewHomeWorkout,
    );
    if (selectionToUse === 'no-exercise-selection') {
      return;
    }

    this.buildStudioExercisesForExerciseSession(
      selectionToUse,
      args.userExerciseSession,
    );
    if (args.gym.enableStudioWeightRecommendations) {
      this.addStudioEquipmentRecommendations(args.userExerciseSession, args.userInfoForEquipmentWeight);
    }
  }

  private async determineStudioActiveWorkoutSelection(
    parentWorkoutSession: UserWorkoutSessionWithDisplayInfo,
    workoutSelectionStrategy: IWorkoutSelectionStrategy,
    settings: WorkoutSettings,
    gym: Gym,
    date: Date,
  ): Promise<WorkoutSelection | undefined> {
    return await workoutSelectionStrategy.getWorkoutSelectionForWorkout(
      settings,
      gym.type,
      date,
      gym.timezone,
      parentWorkoutSession,
    );
  }

  private canChangeInstructor(workoutCount: number, settings: WorkoutSettings) {
    return (
      !WarmupWarmdownConfig.isTutorialWarmupNeeded(workoutCount)
          && settings.cardioOrResistance !== CardioOrResistance.Balance
    );
  }

  private getHydratedHomeSequence(
    activeWorkoutSelection: WorkoutSelection,
    session: UserExerciseSession,
    settings: WorkoutSettings,
    warmUpsAndDowns: Exercise[],
    breaks: Exercise[][],
    homeQuarterValidation: IHomeQuarterZoneValidation,
    user: UserProfile,
    previousExerciseSessions?: UserExerciseSession[],
    playbackSpeed?: number,
  ): SessionSequence[] {
    const exerciseSelection = this.getHomeExerciseSelection(
      activeWorkoutSelection,
      session,
      settings,
      warmUpsAndDowns,
      breaks,
    );
    this.hydrateHomeExerciseSessionExerciseForSelection(exerciseSelection, session, user, settings);
    const quarter = this.homeQuarterCalculator.getQuarterForZone(exerciseSelection.zone, homeQuarterValidation);
    const exerciseFormatDescription = this.getExerciseFormatDescription(exerciseSelection.format);

    return SessionSequenceStrategyContext.getSessionSequence(
      exerciseSelection.format,
      session.exercise,
      session.alternateExercise,
      GymTypes.Home,
      session.secondAlternateExercise,
      session.thirdAlternateExercise,
      exerciseSelection.zone,
      quarter,
      settings,
      previousExerciseSessions,
      exerciseFormatDescription,
      playbackSpeed,
    );
  }

  private addPreWarmupTalkSequenceIfNeeded(
    sequences: SessionSequenceEntry[],
    workoutSession: UserWorkoutSession,
    playbackSpeed?: number,
  ): void {
    if (!workoutSession.livestreamOptions?.preWorkoutTalk) {
      return;
    }
    const pauseExercise = PauseBreakExerciseSelector.pauseExerciseAtStartOfHome(workoutSession.settings);

    const sequence = SessionSequenceStrategyContext.getSessionSequence(
      ExerciseFormat.PauseAtWorkoutStarting,
      pauseExercise,
      undefined,
      GymTypes.Home,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      playbackSpeed,
    );

    const flattenedSequence = SessionSequenceStrategyContext.flattenSequence(sequence);
    if (flattenedSequence[0] && workoutSession.additionalSecondsBeforeFirstExercise) {
      flattenedSequence[0].durationInMilliseconds = workoutSession.additionalSecondsBeforeFirstExercise;
      sequences.unshift(...flattenedSequence);
    }
  }

  private addPreWarmUpSequence(
    sequences: SessionSequenceEntry[],
    workoutSession: UserWorkoutSession,
    gymType: GymTypes,
    playbackSpeed?: number,
  ): void {
    const pauseExericse = PauseBreakExerciseSelector.pauseExerciseAtStartOfHome(workoutSession.settings);

    const sequence = SessionSequenceStrategyContext.getSessionSequence(
      ExerciseFormat.PauseAtWorkoutStarting,
      pauseExericse,
      undefined,
      GymTypes.Home,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      playbackSpeed,
    );

    const flattenedSequence = SessionSequenceStrategyContext.flattenSequence(sequence);
    this.resetPreWarmupSequenceDurationToIncorporateChangeInEndTime(sequences, workoutSession, flattenedSequence);
    sequences.unshift(...flattenedSequence);
  }

  private resetPreWarmupSequenceDurationToIncorporateChangeInEndTime(
    allSequencesExcludingPreWarmup: SessionSequenceEntry[],
    workoutSession: UserWorkoutSession,
    preWarmupSequence: SessionSequenceEntry[],
  ): void {
    const totalWorkoutSecondsExcludingPreWarmupBasedOnSequences = allSequencesExcludingPreWarmup
      .reduce((acc, sequence) => acc += sequence.durationInMilliseconds, 0);
    const totalWorkoutSecondsIncludingPreWarmupBasedOnStartAndEndTimes = (
      workoutSession.endTime.valueOf() - workoutSession.startTime.valueOf()) / 1000;
    const adjustedTotalSecondsPreWarmup = totalWorkoutSecondsIncludingPreWarmupBasedOnStartAndEndTimes
          - totalWorkoutSecondsExcludingPreWarmupBasedOnSequences;
    preWarmupSequence[0].durationInMilliseconds = adjustedTotalSecondsPreWarmup;
  }

  private buildExercisesForExerciseSession(selectionToUse: ExerciseSelection, session: UserExerciseSession) {
    session.format = selectionToUse.format;
    session.exercise = selectionToUse.exercise;
    if (selectionToUse.alternateExercise) {
      session.alternateExercise = selectionToUse.alternateExercise;
    } else {
      delete session.alternateExercise;
    }
    if (selectionToUse.secondAlternateExercise) {
      session.secondAlternateExercise = selectionToUse.secondAlternateExercise;
    } else {
      delete session.secondAlternateExercise;
    }
    if (selectionToUse.thirdAlternateExercise) {
      session.thirdAlternateExercise = selectionToUse.thirdAlternateExercise;
    } else {
      delete session.thirdAlternateExercise;
    }
  }

  private getFirstStudioExerciseForSession(
    selectionToUse: ExerciseSelection,
    session: UserExerciseSession,
  ) {
    const {
      exercise,
      secondAlternateExercise,
    } = selectionToUse;
    if (!secondAlternateExercise) {
      return exercise;
    }
    if (UserExerciseSession.shouldUseThirdAndFourthExercisesInStudio(session)) {
      return secondAlternateExercise;
    }
    return exercise;
  }

  private getSecondStudioExerciseForSession(
    selectionToUse: ExerciseSelection,
    session: UserExerciseSession,
  ) {
    const {
      alternateExercise,
      thirdAlternateExercise,
      exercise,
    } = selectionToUse;
    if (!alternateExercise && !thirdAlternateExercise) {
      return exercise;
    }
    if (!thirdAlternateExercise) {
      return alternateExercise;
    }
    if (UserExerciseSession.shouldUseThirdAndFourthExercisesInStudio(session)) {
      return thirdAlternateExercise;
    }
    return alternateExercise;
  }

  private addStudioEquipmentRecommendations(session: UserExerciseSession, userInfo: IUserInfoForEquipmentWeight) {
    const equipmentWeightCalclulator = new EquipmentWeightCalculator(
      GymTypes.Indoor,
      this.logger,
      session.format,
    );
    equipmentWeightCalclulator.addEquipmentWeightInfoToUserExerciseSession(
      session,
      userInfo,
      session.parentWorkoutSession.settings,
    );
  }

  private buildStudioExercisesForExerciseSession(
    selectionToUse: ExerciseSelection,
    session: UserExerciseSession,
  ) {
    if (!staticFunctionalAreaZones.includes(session.parentWorkoutSession.areaCategory ?? 'circuit')) {
      session.format = selectionToUse.format;
    }
    session.exercise = this.getFirstStudioExerciseForSession(
      selectionToUse,
      session,
    );
    session.alternateExercise = this.getSecondStudioExerciseForSession(
      selectionToUse,
      session,
    );
  }

  private hydrateHomeExerciseSessionExerciseForSelection(
    selectionToUse: ExerciseSelection,
    session: UserExerciseSession,
    user: UserProfile,
    settings: WorkoutSettings,
  ) {
    this.buildExercisesForExerciseSession(selectionToUse, session);
    const equipmentWeightCalclulator = new EquipmentWeightCalculator(GymTypes.Home, this.logger, session.format);
    equipmentWeightCalclulator.addEquipmentWeightInfoToUserExerciseSession(session, user, settings);
  }

  private getStudioExerciseSelection(
    activeWorkoutSelection: WorkoutSelection | undefined,
    session: UserExerciseSession,
    workoutSettings: WorkoutSettings,
    warmupsAndWarmdowns: Exercise[],
    breaks: Exercise[][],
    gym: Gym,
    isNewHomeWorkout: boolean,
  )
    : ExerciseSelection | 'no-exercise-selection' {
    const pauseSelection = PauseBreakExerciseSelector.pauseExerciseSelection(
      session,
      breaks,
      workoutSettings,
      gym.type,
    );
    if (pauseSelection != null) {
      return pauseSelection;
    }

    const warmupSelection = WarmupAndWarmdownSelector.warmupWarmdownExerciseSelection(
      session,
      warmupsAndWarmdowns,
      workoutSettings,
    );
    if (warmupSelection != null) {
      return warmupSelection;
    }

    return this.defaultZoneSpecificExerciseSelection(activeWorkoutSelection, session, gym);
  }

  private getHomeExerciseSelection(
    activeWorkoutSelection: WorkoutSelection | undefined,
    session: UserExerciseSession,
    workoutSettings: WorkoutSettings,
    warmupsAndWarmdowns: Exercise[],
    breaks: Exercise[][],
  )
    : ExerciseSelection {
    const pauseSelection = PauseBreakExerciseSelector.pauseExerciseSelection(
      session,
      breaks,
      workoutSettings,
      GymTypes.Home,
    );
    if (pauseSelection != null) {
      return pauseSelection;
    }

    const warmupSelection = WarmupAndWarmdownSelector.warmupWarmdownExerciseSelection(
      session,
      warmupsAndWarmdowns,
      workoutSettings,
    );
    if (warmupSelection != null) {
      return warmupSelection;
    }
    return this.popExerciseFromHomeSelection(activeWorkoutSelection, session);
  }

  private defaultZoneSpecificExerciseSelection(
    activeWorkoutSelection: WorkoutSelection | undefined,
    session: UserExerciseSession,
    gym: Gym,
  )
    : ExerciseSelection | 'no-exercise-selection' {
    if (activeWorkoutSelection == null) {
      return this.createFakeExerciseSelection(session);
    }

    const { typeNumber } = session.zone;
    const areaCategory = session.parentWorkoutSession.areaCategory ?? 'circuit';
    if (!hasExerciseForZoneScreen(typeNumber)) {
      this.logger.debug(`${session.parentWorkoutSession.uniqueReference} is a machine area session, wont hydrate`);
      return 'no-exercise-selection';
    }
    if (staticFunctionalAreaZones.includes(areaCategory)) {
      return activeWorkoutSelection.exerciseSelections[
        this.getIndexForExerciseSessionBeforeCurrentStage(session, areaCategory)
      ];
    }
    const selectionForZone = activeWorkoutSelection.exerciseSelections
      .find((s) => s.zone === typeNumber);
    return selectionForZone as ExerciseSelection;
  }

  private getIndexForExerciseSessionBeforeCurrentStage(session: UserExerciseSession, areaCategory: AreaCategory) {
    const stageIndex = session.getIndexForCurrentExercise();
    const longFunctionalStaticIndexAdjustmentForRepeatingSequences = Math.ceil(stageIndex / 2);
    const isLongFunctionalStaticWorkout = isStaticFunctionalZone(areaCategory)
      && session.parentWorkoutSession.workoutType === WorkoutType.FullStudioWorkout;

    return isLongFunctionalStaticWorkout
      ? stageIndex - longFunctionalStaticIndexAdjustmentForRepeatingSequences
      : stageIndex;
  }

  private popExerciseFromHomeSelection(
    activeWorkoutSelection: WorkoutSelection | undefined,
    session: UserExerciseSession,
  ): ExerciseSelection {
    if (!activeWorkoutSelection) {
      return this.createFakeExerciseSelection(session);
    }
    return activeWorkoutSelection.exerciseSelections.shift() as ExerciseSelection;
  }

  private createFakeExerciseSelection(session: UserExerciseSession): ExerciseSelection {
    const fakeForTesting = ExerciseSelection.fromUserExerciseSession(session);
    fakeForTesting.exercise = new Exercise(
      '',
      '',
      [],
      false,
      [CardioOrResistance.Cardio],
      [],
      [],
      [],
      [fakeForTesting.format],
    );
    return fakeForTesting;
  }

  private getEndTimeAdjustToUserDeviceTime(
    endTimeAsDate: Date,
    userDeviceTimeAsDate: Date,
    serverRequestTimeAsDate: Date,
  ): Date {
    const endTime = endTimeAsDate.getTime();
    const userDeviceTime = userDeviceTimeAsDate.getTime();
    const serverRequestTime = serverRequestTimeAsDate.getTime();
    const timeDifference = userDeviceTime - serverRequestTime;
    return new Date(endTime + timeDifference);
  }

  private async getHomeInstructorForToday(workoutStartDate: Date) {
    const availableInstructors = await this.workoutRepository.getAllActiveInstructors();
    const dateSeed = PseudoRandomGenerator.getSeedFromDate(workoutStartDate);
    const randomInstructors = PseudoRandomGenerator.shuffleArrayPseudoRandomly(availableInstructors, dateSeed);
    const randomInstructor = randomInstructors[0];
    this.logger.info(`Using random instructor for today: ${randomInstructor}`);
    return randomInstructor;
  }

  private checkIfInstructorSetOnWorkout(workoutSettings: WorkoutSettings) {
    const instructor = workoutSettings.homeWorkoutInstructor;
    return instructor && instructor !== '' ? instructor : false;
  }

  private async checkIfInstructorSetOnUserProfile(userProfileId: string) {
    const userProfile = await this.userProfileRepository.get(userProfileId);
    if (!userProfile) { return false; }
    const instructor = userProfile.homeWorkoutInstructor;
    return instructor && instructor !== '' ? instructor : false;
  }

  private setInstructorOnWorkoutSettings(settings: WorkoutSettings, instructor: any) {
    if (settings.cardioOrResistance === CardioOrResistance.Balance) {
      settings.homeWorkoutInstructor = 'Eliza';
      return;
    }
    if (!settings.homeWorkoutInstructor || settings.homeWorkoutInstructor === 'Eliza') {
      settings.homeWorkoutInstructor = instructor;
    }
  }

  private async getUserWorkoutSessionForWorkoutSelection(userWorkoutSession: UserWorkoutSession):
  Promise<UserWorkoutSession | undefined> {
    if (!userWorkoutSession.pairedSessionReference) {
      return userWorkoutSession;
    }
    const inviterWorkout = await this.workoutRepository.getWorkoutSessionFromId(
      userWorkoutSession.pairedSessionReference,
    );
    if (inviterWorkout && WorkoutSettings.equals(inviterWorkout.settings, userWorkoutSession.settings)) {
      return inviterWorkout;
    }
    return userWorkoutSession;
  }

  private getSuperSetText(maxSupersets: number) {
    let supersetText = '';
    if (this.superSetRepetition === 1) {
      supersetText = `Starting superset ${this.superSetRepetition}/${maxSupersets}`;
    } else {
      supersetText = `Repeating superset ${this.superSetRepetition}/${maxSupersets}`;
    }
    if (this.superSetRepetition === maxSupersets) {
      this.superSetRepetition = 1;
    } else {
      this.superSetRepetition += 1;
    }
    return supersetText;
  }

  private getExerciseFormatDescription(exerciseFormat: ExerciseFormat): string | undefined {
    if (exerciseFormat === ExerciseFormat.BuildQuadSuperset4Min
            || exerciseFormat === ExerciseFormat.BuildQuadSupersetQuicksetup4Min) {
      const maxSupersets = 3;
      return this.getSuperSetText(maxSupersets);
    }
    if (exerciseFormat === ExerciseFormat.BurnQuadSuperset3Min
            || exerciseFormat === ExerciseFormat.BurnQuadSupersetQuicksetup3Min) {
      const maxSupersets = 2;
      return this.getSuperSetText(maxSupersets);
    }
    return undefined;
  }

  private getWorkoutSelectionStrategy(
    userWorkoutSession: UserWorkoutSession,
  ): IWorkoutSelectionStrategy {
    if (userWorkoutSession.isStreamingWorkout) {
      return new InfluencerWorkoutSelectionStrategy(
        this.workoutRepository,
        this.currentWorkoutSelectionManager,
        this.workoutSelectionManager,
      );
    }
    return new UserBasedWorkoutSelectionStrategy(
      this.workoutRepository,
      this.currentWorkoutSelectionManager,
      this.workoutSelectionManager,
    );
  }
}
